// WS-16: the winter ground (resort.json -> ground.look). Engine-generic and OFF when the key is absent (the
// painted roads, paths and snow of paintGround() stay as they were).
//
// What the client's winter drone photos show (refs/photos/winter-ready/Drone Property *), and how it is drawn:
//  - ploughed roads: compacted grey-white snow with two darker, glazed tyre tracks, soft uneven edges and a
//    plough bank of fresh snow on each side (banks open where another road or path joins);
//  - footpaths: a narrow band of trodden snow (footprint mottling), no edge line;
//  - groomed trails: cross-country ski trails (classic track pairs + a corduroy skate lane, skiLook) and snowmobile
//    tracks (tread bands), where OSM maps them or a person draws one in Fine-tune; where lines overlap the harder
//    surface shows (HARD);
//  - ice paths (a skating lane drawn in Fine-tune, path kind 'ice'): smooth glossy blue-grey ice level with the snow,
//    faint skate scratches, a frosted edge; a footpath outside winter (drawnWay);
//  - the snow itself: drifts and wind ripples (world-space shader detail, so it stays sharp at any zoom),
//    a blue shade under dense canopy painted from the survey trees (vegetation.js), no flat empty plane.
// Roads, paths and trails are ONE ribbon mesh (one draw call) that follows the terrain; its shader draws the
// surface from the across/along coordinates, so the detail does not depend on the 3 px/m ground texture.
// WS-75: node materials (TSL). Every shading here is a hook of gpu/chain.js on a MeshStandardNodeMaterial, at a
// numbered stage, so the snow shading (materials.js), water, the far canopy
// (landscape.js), lazy/groundlook.js and light casting compose on one material in a fixed order:
//   colour stages: 10 bare floor / detail layers / ribbon surfaces, 15 ribbon detail, 20 far canopy, 30 water, 40 snow relief
//   normal stages: 10 detail / ribbon detail, 20 snow relief, 30 water ripples
//   lighting: gpu/lighting.js addLightHook (materials lane): cast light at its `lights` stage (gpu/lightcast.js),
//   snow shading, then glints + cold glow and the glaze sheen (groundlook) at `finish`
import * as THREE from 'three/webgpu';
import { If, float, int, vec2, vec3, vec4, uniform, texture, attribute, property, positionWorld, positionView, cameraViewMatrix, diffuseColor, specularColor, specularColorBlended,
  fwidth, dFdx, dFdy, mix, smoothstep, clamp, min, max, abs, sign, floor, length, normalize, dot, sin, step, select, uv, renderGroup, sRGBTransferEOTF, materialColor, screenCoordinate } from 'three/tsl';
import { grdNoise, grdFbm } from './gpu/ground-noise.js';
import { ign } from './gpu/dither.js';
import { onColor, onNormal, onRough, onVariants, onLight, pname } from './gpu/chain.js';
import { DETAIL_LAYERS } from './groundpaint.js'; // VIS-267 W6: the ground canvas paint (canopy shade, road underlay, splat map) lives there
import { releaseCpu } from './gpu/release.js'; // VIS-267 round 2: releaseRibbons
import { uploaded, isPreCompiling } from './gpu/internals.js';
import { drewAll, dryDraw } from './gpu/pipelines.js'; // bakedAt: a draw three skipped stays to do; drawn dry inside a compile
import { lineDist, inRing, stretchDist, bandLine, resampleEven } from './lines/measure.js'; // VIS-197: the shared sums of every line (VIS-228: + bandLine, resampleEven)

export const LUMA = vec3(0.2126, 0.7152, 0.0722);
/** A world-space tilt (gnW, ~up) applied to the view-space normal n: normalize(n + (view(gnW) - view(up))). */
export const tiltNormal = (n, gnW) => normalize(n.add(normalize(cameraViewMatrix.mul(vec4(normalize(gnW), 0.0)).xyz).sub(cameraViewMatrix.mul(vec4(0.0, 1.0, 0.0, 0.0)).xyz)));
const setRGB = (rgb) => diffuseColor.assign(vec4(rgb, diffuseColor.a));

// WS-63 ground: the new versions of the ground (ground.look snow.pattern/relief/grain/glints/glow, roads.irregular/tyres/
// finish, lots, sledding, paint[] surface "sledding") live in lazy/groundlook.js, which fills HOOK when main.js fetches
// it (only for a map that sets one of those keys). Empty HOOK = today's ground, byte for byte.
export const HOOK = {};
export function lookOf(cfg = {}) {
  const r = cfg.roads || {}, p = cfg.paths || {}, s = cfg.snow || {}, c = cfg.colors || {};
  return { x: cfg, // WS-63 ground: the raw keys, for HOOK
    roadWidth: r.width || { service: 4.2, residential: 5.6, unclassified: 5.6, default: 5.6 },
    bankH: r.banks ?? 0.45, bankW: r.bankWidth ?? 1.7, ruts: r.ruts ?? 0.6, gauge: r.gauge ?? 0.85, bank: bankShape(r.bank),
    pathWidth: p.width ?? 1.5, trackWidth: p.trackWidth ?? 2.6, trails: cfg.trails !== false,
    drifts: s.drifts ?? 0.5, ripples: s.ripples ?? 0.5, canopyShade: cfg.canopyShade ?? 0.5,
    wind: s.wind || [0.8, 0.6], // prevailing wind (x east, z south): ripples run across it
    lowSun: s.lowSun || null, // WS-82: ripples eased under a low sun (setSnowSun); absent = today's
    ski: skiLook(cfg.ski),
    colors: {
      road: c.road || '#c3cad3', rut: c.rut || '#929eae', bank: c.bank || '#f5f8fb', bankInner: c.bankInner || '#d9dfe7',
      path: c.path || '#cdd4dd', trail: c.trail || '#dce2ea', shade: c.shade || 'rgba(112,128,158,1)', ice: c.ice || '#5f7f96', // ice: an ice path's lane (winter): grey-cyan, darker than the snow and bluer-green than its shade
    },
  };
}

// The groomed cross-country ski trail, ground.look.ski (every key optional). Where its classic tracks lie follows the
// trail's width as a groomer lays them: under 2.5 m one pair of grooves in the middle (a classic-only forest track),
// up to 4.5 m one pair along the right-hand edge and a skate lane beside it, wider a pair along each edge and the skate
// lane between them.
// The defaults are the owner's pick of 2026-10-25 (look B 'clear', over A quiet and C bold: "clearest and best USP"):
// freshly groomed and bright, a crisp cut edge, the classic grooves clearly shaded.
//   grooves  1.2   how dark the classic tracks' grooves show (0 none, 1 a clear shaded groove)
//   cord     0.8   the corduroy of the skate lane (fine ribs along the trail; it averages out from afar)
//   tone     1.04  the groomed lane's brightness against the trail colour (1 = ground.look.colors.trail)
//   edge     0.15  m over which the groomed edge fades into the snow beside it (small = a crisp cut edge)
export function skiLook(k = {}) {
  return { grooves: k.grooves ?? 1.2, cord: k.cord ?? 0.8, tone: k.tone ?? 1.04, edge: Math.max(0.05, k.edge ?? 0.15) };
}

// VIS-228: the plough bank's cross-section, ground.look.roads.bank (every key optional; absent = the bank as it always
// was: a ridge with straight faces):
//   crest     0.45  where the crest sits, as a share of bankWidth out from the lane's edge (low = a long outer slope)
//   shoulder  0.25  the lift share of the step right at the lane's edge (0 = the bank rises from the lane itself)
//   round     0     0 = straight faces; 1 = a rounded mound whose foot meets the field and the lane level (no edge
//                   to catch the light); any value > 0 adds three columns per bank (one inside, two outside the crest)
//   fade      0.45  m of the bank's outer foot that fades into the snow beside it
const clamp01 = (v) => Math.min(1, Math.max(0, v));
export function bankShape(b) {
  const o = b && typeof b === 'object' ? b : {};
  return { crest: Math.min(0.85, Math.max(0.15, o.crest ?? 0.45)), shoulder: clamp01(o.shoulder ?? 0.25), round: clamp01(o.round ?? 0), fade: Math.max(0.1, o.fade ?? 0.45) };
}
const BANK_STEP = 0.12; // m: the shoulder's width at the lane's edge
/** Lift share (0..1) of a plough bank e m out from the lane's edge, for a bank BW m wide of shape B (bankShape). */
export function bankLift(e, BW, B) {
  if (!(e > 0) || e >= BW) return 0;
  const st = Math.min(BANK_STEP, BW * 0.2), c = Math.max(st + 0.05, B.crest * BW);
  if (e < st) return (B.shoulder * e) / st;
  if (e < c) { const u = (e - st) / (c - st); return B.shoulder + (1 - B.shoulder) * (u + (Math.sin((u * Math.PI) / 2) - u) * B.round); }
  const u = Math.min(1, (e - c) / Math.max(0.01, BW - c));
  return 1 - u + ((1 + Math.cos(u * Math.PI)) / 2 - (1 - u)) * B.round;
}
/** The columns of one bank, from the lane's edge out: [m out from the edge, lift share]. Straight faces: the shoulder,
 *  the crest and the foot (the cross-section of old); rounded: one more inside the crest and two outside. */
export function bankColumns(BW, B) {
  const st = Math.min(BANK_STEP, BW * 0.2), c = Math.max(st + 0.05, B.crest * BW);
  const es = B.round > 0 ? [st, st + (c - st) * 0.5, c, c + (BW - c) * 0.35, c + (BW - c) * 0.75, BW] : [st, c, BW];
  return es.map((e) => [e, e === c ? 1 : bankLift(e, BW, B)]);
}

// ------------------------------------------------------------------ ground texture paint (canvas)
// VIS-267 W6: the canopy shade, the road underlay and the WS-42 control (splat) map painter are in groundpaint.js,
// which paints the core ground canvas in a worker (main.js paintGround).

// ------------------------------------------------------------------ WS-35: season palette (bare ground)
// The ground canvas (main.js paintGround) is painted in the season's tones from the same survey layout: base floor,
// soft mottle patches (lichen, heather, crowberry, grass), area fills, marsh sedge, water and stream underlay, and
// optional clearings (sand, gravel yards) traced from a reference. Winter keeps the original snow paint exactly.
// No image is draped (user 2026-10-12: a 0.5 m orthophoto on the ground reads low-res).
// resort.json -> ground.palette (usually inside seasonal.<season>): { base, mottle: [{ tone: 'r,g,b', a, n, r: [min, max] }],
//   areas: { farmland, meadow, grass, parking, helipad, marsh }, sedge, water, streams, paint, clearings: [{ polygon, color, blur }],
//   detail: { light, dark, amount, scale } }
const WINTER_PALETTE = {
  base: '#f1f5fa', mottle: null, sedge: 'rgba(120,96,70,0.45)', sedgeWet: 'rgba(132,112,92,0.3)', water: '#eef3f9', streams: '#c6d8ea', paint: '#dde3eb',
  areas: { farmland: '#f7f9fc', meadow: '#f6f8fb', grass: '#f4f7fb', parking: '#dfe5ec', helipad: '#e3e8ef', marsh: '#eef3f8' }, clearings: [], detail: null,
};
export function paletteOf(cfg, season = 'winter') {
  if (season === 'winter' || !cfg) return { ...WINTER_PALETTE, winter: true }; // no palette: the snow paint (+ the old season tint in main.js)
  return { ...WINTER_PALETTE, ...cfg, areas: { ...WINTER_PALETTE.areas, ...(cfg.areas || {}) }, clearings: cfg.clearings || [], winter: false };
}

// ------------------------------------------------------------------ WS-42: bare-ground material (detail layers + splat)
// Round-7 feedback: the bare ground was "blurry, no good material" (a 3 px/m canvas + smooth noise). The ground is now
// six tiling detail layers generated at load (ground-detail.js: lichen, shrub, litter, grass, gravel, sand; normals,
// albedo detail, accent mask), blended per pixel by a control (splat) map painted from the SAME survey layout as the
// colour canvas (base mix, mottle patches, canopy litter, fields, clearings, roads), with a height blend so layer edges
// are crisp. The canvas keeps the macro colour: the detail only multiplies it by (layer colour x detail) / (mean
// layer colour), so from afar (mips -> mean) the ground is exactly the canvas and the far landscape still matches it.
// No image drape, no extra draw calls. Config: ground.palette.layers (per season):
//   { base: { lichen, shrub, litter, grass, gravel, sand } (weights of the open floor), colors: { <layer>: '#hex' },
//     accent: { <layer>: ['#hex', strength] }, tile (m), amount (0..1), normal (0..2), contrast (height blend) }
// Layers are uniforms (colours, accents, amount, weights): a live season switch only calls setDetailLayers().
export { DETAIL_LAYERS }; // (defined with the paint, groundpaint.js: the worker paints the splat map by these names)
export const DETAIL_TILE = 1.6; // m per tile (ground-detail.js TILE)
const LAYER_DEFAULTS = {
  tile: DETAIL_TILE, amount: 1, normal: 1, contrast: 0.5,
  base: { lichen: 0.35, shrub: 0.5, litter: 0.15 },
  colors: { lichen: '#b4b294', shrub: '#3d4626', litter: '#6a5636', grass: '#6c7c3e', gravel: '#9c988c', sand: '#b9a37c' },
  accent: {},
};
export function layersOf(palette) {
  const L = palette?.layers;
  if (!L || palette.winter) return null;
  return { ...LAYER_DEFAULTS, ...L, base: { ...(L.base || LAYER_DEFAULTS.base) }, colors: { ...LAYER_DEFAULTS.colors, ...(L.colors || {}) }, accent: { ...(L.accent || {}) } };
}
const weightsOf = (base) => DETAIL_LAYERS.map((k) => base?.[k] ?? 0);

/** The detail texture array (uniform object shared by every bare-ground material). Starts neutral (128, 128, 128, 0
 *  = no detail), filled when the worker has built the layers. Returns { uniform, ready: Promise }.
 *  WS-75: `uniform` is a TextureNode (its .value is the texture, as before) that every material samples clones of
 *  (.sample().depth().grad()). The texture is made at its final size and filled in place: three r186 (WebGPU) kept
 *  drawing the first texture after .value was swapped for the worker's (a 1x1 neutral one: no detail at all).
 *  VIS-233 phone set (owner 2026-10-21: "smaller ground texture is fine"): 256 px layers instead of 512, a quarter of
 *  the graphics memory (with mips ~2.1 MB instead of ~8.4 MB); the same two scales and filtering as on a computer
 *  (anisotropy costs no memory), so only the finest grain is softer. */
export function createDetailTextures({ mobile = false } = {}) {
  const n = DETAIL_LAYERS.length, N = mobile ? 256 : 512, data = new Uint8Array(N * N * 4 * n);
  new Uint32Array(data.buffer).fill(new Uint32Array(new Uint8Array([128, 128, 128, 0]).buffer)[0]); // WS-77: the neutral texel (128, 128, 128, 0) in one fill, not 1.5 M small copies
  const t = new THREE.DataArrayTexture(data, N, N, n);
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true; t.anisotropy = 8; t.needsUpdate = true;
  const uniform = texture(t);
  const use = (r) => {
    if (!r || r.error || r.N !== N || r.layers !== n) return;
    t.image.data = r.data; t.needsUpdate = true; // WS-77: the worker's own array (transferred), not a 6 MB copy into the neutral one
    // WS-77: once the layers are on the graphics chip (mips made there) the 6 MB JS copy is dropped; nothing uploads
    // this texture again (the layers never change; a lost device reloads the page)
    t.onUpdate = () => { t.onUpdate = null; t.image.data = null; };
    uniform.ms = r.ms;
  };
  const ready = new Promise((resolve) => {
    const fallback = () => import('./ground-detail.js').then((m) => { use(m.buildGroundDetail(N)); resolve(); });
    try {
      const w = new Worker(new URL('./ground-detail-worker.js', import.meta.url), { type: 'module' });
      w.onmessage = (e) => { w.terminate(); if (e.data?.error) fallback(); else { use(e.data); resolve(); } };
      w.onerror = () => { w.terminate(); fallback(); };
      w.postMessage({ N });
    } catch { /* errors:ignore: no module workers: build on the main thread */ fallback(); }
  });
  return { uniform, ready };
}

// WS-75: the WS-42 layer blend in TSL: macro canvas colour x layer detail ratio, accents; the layer normals (world xz
// offsets) reach the normal stage through a property.
/** Uniforms for a layer config (shared by core, far, zone and ribbon materials so a season switch updates all).
 *  Uniform nodes (each keeps .value); uGdCol / uGdAcc / uGdAccA are arrays of them, one per layer; ctlTex = the two
 *  control (splat) textures or nulls, read when a material is built. */
export function detailUniforms(layers, tex, ctl = null, extent = null) {
  const u = {
    uGdTex: tex, ctlTex: [ctl?.[0] || null, ctl?.[1] || null],
    uGdB: uniform(extent ? new THREE.Vector4(extent.minX, extent.minZ, extent.maxX, extent.maxZ) : new THREE.Vector4(0, 0, 1, 1)),
    uGdW0: uniform(new THREE.Vector3()), uGdW1: uniform(new THREE.Vector3()),
    uGdCol: DETAIL_LAYERS.map(() => uniform(new THREE.Color())), uGdAcc: DETAIL_LAYERS.map(() => uniform(new THREE.Color())),
    uGdAccA: DETAIL_LAYERS.map(() => uniform(0)),
    uGdP: uniform(new THREE.Vector4(DETAIL_TILE, 0, 1, 0.5)), // P = (tile m, amount, normal, contrast)
  };
  setDetailLayers(u, layers);
  return u;
}
/** WS-42 (live season switch): point the detail uniforms at another season's layers (null = no detail). */
export function setDetailLayers(u, layers) {
  const L = layers || LAYER_DEFAULTS, w = weightsOf(L.far || L.base), s = w.reduce((a, b) => a + b, 0) || 1;
  u.uGdW0.value.set(w[0] / s, w[1] / s, w[2] / s); u.uGdW1.value.set(w[3] / s, w[4] / s, w[5] / s);
  DETAIL_LAYERS.forEach((k, i) => {
    u.uGdCol[i].value.set(L.colors[k] || LAYER_DEFAULTS.colors[k]);
    const a = L.accent?.[k]; u.uGdAcc[i].value.set(a?.[0] || '#000000'); u.uGdAccA[i].value = a?.[1] ?? 0;
  });
  u.uGdP.value.set(L.tile ?? DETAIL_TILE, layers ? (L.amount ?? 1) : 0, L.normal ?? 1, L.contrast ?? 0.5);
}
/** The second, rotated detail scale: mat2(0.8, 0.6, -0.6, 0.8) * p. */
const rot2 = (p) => vec2(p.x.mul(0.8).sub(p.y.mul(0.6)), p.x.mul(0.6).add(p.y.mul(0.8)));
/** One layer's detail texel (R, G = normal xz, B = albedo detail, A = accent mask), two scales. */
function layerTap(tex, i, t1, t2) {
  const a = tex.sample(t1.uv).depth(i).grad(t1.dx, t1.dy);
  const b = tex.sample(t2.uv).depth(i).grad(t2.dx, t2.dy); // second scale breaks the repeat
  return vec4(a.rg.add(b.rg.sub(0.5).mul(0.6)), a.b.mul(b.b.mul(0.8).add(0.6)), max(a.a, b.a.mul(0.5)));
}

/** WS-35 / WS-42: bare (non-snow) ground material. With `detail` ({ uniforms from detailUniforms, ctl: bool })
 *  it draws the layer material above; without it, the WS-35 fine noise mottle (three noise scales tint the canvas
 *  towards palette.detail.light / .dark). No extra draw calls either way. */
export function patchFloor(material, palette, detail = null) {
  if (detail) return patchDetail(material, detail);
  const u = { uFlLight: uniform(new THREE.Color()), uFlDark: uniform(new THREE.Color()), uFlAmt: uniform(0), uFlScale: uniform(1) };
  setFloor(u, palette);
  onColor(material, () => {
    const fp = positionWorld.xz.div(u.uFlScale).toVar();
    const px = length(fwidth(fp)); // metres per pixel: fine scales fade before they alias
    const f3 = smoothstep(0.08, 0.3, px).oneMinus().toVar();
    const n1 = grdFbm(fp.mul(0.18)).toVar();                                                // ~5 m clumps
    const n2 = grdNoise(fp.mul(0.9).add(4.7)).mul(smoothstep(0.25, 0.9, px).oneMinus()).toVar(); // ~1 m tufts
    const n3 = grdNoise(fp.mul(3.1).add(9.3)).mul(f3).toVar();                             // ~0.3 m speckle
    const lt = smoothstep(0.58, 0.8, n1.mul(0.55).add(n2.mul(0.3)).add(n3.mul(0.15)));
    const dk = smoothstep(0.55, 0.78, n1.oneMinus().mul(0.5).add(n2.mul(0.35)).add(n3.oneMinus().mul(0.15)));
    const base = diffuseColor.rgb.toVar();
    const c = mix(base, base.mul(0.5).add(u.uFlLight.mul(0.5)), lt.mul(u.uFlAmt));
    const c2 = mix(c, base.mul(0.45).add(u.uFlDark.mul(0.55)), dk.mul(u.uFlAmt).mul(0.9));
    setRGB(c2.mul(n3.mul(0.12).mul(f3).add(0.94)));
  }, 10);
  material.userData.floor = u;
  return u;
}
/** WS-77: point a fine-mottle floor (patchFloor without layers) at another season's palette (one material set for
 *  the bare seasons, main.js setSeason). */
export function setFloor(u, palette) {
  const d = palette.detail || {};
  u.uFlLight.value.set(d.light || '#b9b597'); u.uFlDark.value.set(d.dark || '#3f4228'); u.uFlAmt.value = d.amount ?? 0.35; u.uFlScale.value = d.scale ?? 1;
}
function patchDetail(material, { uniforms: u, ctl = false }) {
  const gdN = property('vec2', pname('gdN'));
  onColor(material, () => {
    const gp = positionWorld.xz.toVar();
    const gpx = length(fwidth(gp)).toVar(); // metres per pixel
    const P = u.uGdP;
    let w0 = u.uGdW0, w1 = u.uGdW1;
    if (ctl && u.ctlTex[0] && u.ctlTex[1]) {
      const B = u.uGdB, cq = gp.sub(B.xy).div(max(B.zw.sub(B.xy), vec2(1.0))).toVar();
      const inside = cq.x.greaterThanEqual(0.0).and(cq.y.greaterThanEqual(0.0)).and(cq.x.lessThanEqual(1.0)).and(cq.y.lessThanEqual(1.0));
      const cc = clamp(cq, 0.0, 1.0);
      w0 = select(inside, texture(u.ctlTex[0], cc).rgb, u.uGdW0); w1 = select(inside, texture(u.ctlTex[1], cc).rgb, u.uGdW1);
    }
    const W0 = vec3(w0).toVar(), W1 = vec3(w1).toVar(); // (the control taps stay here: implicit derivatives need uniform control flow)
    const t1u = gp.div(P.x).toVar(), t1 = { uv: t1u, dx: dFdx(t1u).toVar(), dy: dFdy(t1u).toVar() };
    const t2u = rot2(gp).div(P.x.mul(3.7)).add(vec2(0.37, 0.71)).toVar(), t2 = { uv: t2u, dx: dFdx(t2u).toVar(), dy: dFdy(t2u).toVar() };
    gdN.assign(vec2(0.0));
    // WS-77: the detail's weight below (gAmt) is exactly 0 from 0.35 m per pixel: skip it there (most of a home or far
    // view; the far mesh almost everywhere). Every derivative is taken above; the taps inside use explicit gradients.
    If(gpx.lessThan(0.35), () => detailLayers(u, W0, W1, t1, t2, gpx, gdN));
  }, 10);
  onNormal(material, (nv) => tiltNormal(nv, vec3(gdN.x, 1.0, gdN.y)), 10); // WS-42: layer normals tilt the terrain normal
  material.userData.groundDetail = u;
  return u;
}
/** The WS-42 layer blend of one pixel (patchDetail, inside its distance branch): colour into diffuseColor, normal tilt into gdN. */
function detailLayers(u, W0, W1, t1, t2, gpx, gdN) {
  {
    const P = u.uGdP;
    const ws = max(W0.x.add(W0.y).add(W0.z).add(W1.x).add(W1.y).add(W1.z), 1e-3).toVar();
    const W = [W0.x, W0.y, W0.z, W1.x, W1.y, W1.z].map((w) => w.div(ws).toVar());
    const hk = P.w.mul(smoothstep(0.015, 0.09, gpx).oneMinus()).toVar(); // height blend only where the texels resolve
    const S = [], sc = [];
    let smax = float(-9.0);
    for (let i = 0; i < 6; i++) {
      const on = W[i].greaterThan(0.01);
      S[i] = vec4(0.5, 0.5, 0.5, 0.0).toVar();
      If(on, () => { S[i].assign(layerTap(u.uGdTex, int(i), t1, t2)); });
      sc[i] = select(on, W[i].add(S[i].b.mul(hk)), -9.0).toVar();
      smax = max(smax, sc[i]);
    }
    smax = smax.toVar();
    const B = sc.map((v) => max(v.sub(smax).add(0.12), 0.0).toVar());
    const bs = max(B.reduce((s, v) => s.add(v)), 1e-4).toVar();
    const bk = step(1e-3, hk).mul(clamp(hk.div(max(P.w, 1e-3)), 0.0, 1.0)).toVar();
    let meanC = vec3(0.0), detC = vec3(0.0), accC = vec3(0.0), acc = float(0.0), n = vec2(0.0);
    for (let i = 0; i < 6; i++) {
      const b = mix(W[i], B[i].div(bs), bk).toVar(), sb2 = S[i].b.mul(2.0);
      meanC = meanC.add(u.uGdCol[i].mul(W[i]));
      detC = detC.add(u.uGdCol[i].mul(b).mul(sb2));
      const ai = b.mul(S[i].a).mul(u.uGdAccA[i]).toVar();
      accC = accC.add(u.uGdAcc[i].mul(ai).mul(sb2)); acc = acc.add(ai);
      n = n.add(S[i].rg.mul(2.0).sub(1.0).mul(b));
    }
    meanC = meanC.toVar(); acc = acc.toVar();
    const gBase = diffuseColor.rgb.toVar();
    const gAmt = P.y.mul(smoothstep(0.06, 0.35, gpx).oneMinus()).toVar(); // from afar the canvas alone: no tile can repeat across a view
    const ratio = clamp(detC.div(max(meanC, vec3(1e-3))), vec3(0.2), vec3(2.6));
    const c1 = gBase.mul(mix(vec3(1.0), ratio, gAmt));
    const lr = dot(gBase, LUMA).div(max(dot(meanC, LUMA), 1e-3));
    setRGB(mix(c1, accC.div(max(acc, 1e-4)).mul(clamp(lr, 0.3, 2.0)), clamp(acc, 0.0, 1.0).mul(gAmt)));
    gdN.assign(n.mul(P.z).mul(gAmt));
  }
}

/** WS-42: gravel roads and earth paths on the ribbon mesh (non-winter): the gravel / sand layer modulates the
 *  ribbon colour (detail around its mean) and tilts the normal. Uses the same detail uniforms. */
export function patchRibbonDetail(mat, { uniforms: u }) {
  const gdN = property('vec2', pname('gdR'));
  onColor(mat, () => { // road = gravel layer, footpath = sand/earth, track = both
    const k = floor(attribute('aKind', 'float').add(0.5)), rw = positionWorld.xz.toVar(), P = u.uGdP;
    const gl = select(k.lessThan(0.5).or(k.greaterThan(3.5)), int(4), int(5));
    const t1u = rw.div(P.x).toVar(), d1x = dFdx(t1u).toVar(), d1y = dFdy(t1u).toVar();
    const t2u = rot2(rw).div(P.x.mul(3.7)).add(0.37).toVar(), d2x = dFdx(t2u).toVar(), d2y = dFdy(t2u).toVar();
    const rpx = length(fwidth(rw)).toVar();
    gdN.assign(vec2(0.0));
    If(rpx.lessThan(0.35), () => { // WS-77: ga below is exactly 0 from 0.35 m per pixel: no taps there (derivatives above)
      const s1 = u.uGdTex.sample(t1u).depth(gl).grad(d1x, d1y), s2 = u.uGdTex.sample(t2u).depth(gl).grad(d2x, d2y); // the second scale breaks the repeat
      const s = vec4(s1.rg.add(s2.rg.sub(0.5).mul(0.6)), s1.b.mul(s2.b.mul(0.8).add(0.6)), s1.a).toVar();
      const ga = P.y.mul(smoothstep(0.06, 0.35, rpx).oneMinus()).toVar();
      setRGB(diffuseColor.rgb.mul(mix(1.0, s.b.mul(2.0), ga)));
      gdN.assign(s.rg.mul(2.0).sub(1.0).mul(P.z).mul(ga));
    });
  }, 15);
  onNormal(mat, (nv) => tiltNormal(nv, vec3(gdN.x, 1.0, gdN.y)), 10);
  mat.userData.groundDetail = u;
}

// ------------------------------------------------------------------ snow detail on the ground material
// ---- WS-78 lane G (begin): the still snow relief kept in a texture ------------------------------------------------
// The drifts never change (world-space noise of fixed numbers), but they were worked out per pixel per frame: 12 value
// noises (3 drift samples for the slope) on every ground pixel, ~3-4 ms of a laptop chip's frame at the home view.
// Now they are worked out ONCE, by the same shader functions on the graphics chip, into a world-space texture over the
// mesh's ground (1 m texels, mipmapped; the finest drift cell is ~5 m, so the texture holds it to ~1 % of its
// amplitude), and every frame reads one texel. Kind "kept" (performance.md): its only inputs are the relief's numbers
// and the ground mesh's extent (both fixed for a material's life; another relief = another texture, cached by key).
// A ground larger than BAKE_MAX texels at 1.25 m keeps the per-pixel noise.
const BAKE_MAX = 2048, BAKE_TEXEL = 1, BAKE_TEXEL_MAX = 1.25, BAKE_SLOPE = 3.5; // slope (D(q + e) - D(q)) is within +-0.15: x3.5 + 0.5 fits 0..1
const BAKES = new Map(); // key -> { node: RTTNode, B: Vector4 (x0, z0, 1 / width m, 1 / depth m) }
/** A world-space RGBA8 texture of fn(world xz) -> vec4 (0..1) over the extent of the mesh being built (b.object), made
 *  once on the graphics chip at its first frame, `texel` m a texel. Returns the texel at `gp` (a node), or null (no mesh / too large).
 *  (Exported for lazy/groundlook.js's "smooth" and "powder" snow, which keep their relief the same way.) */
export function bakedAt(b, key, fn, gp, texel = BAKE_TEXEL) {
  const o = b?.object, g = o?.geometry;
  if (!g?.attributes?.position) return null;
  if (!g.boundingBox) g.computeBoundingBox();
  const bb = g.boundingBox.clone().applyMatrix4(o.matrixWorld);
  const span = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) + 2;
  const t = Math.max(texel, span / BAKE_MAX);
  if (!(t <= texel * BAKE_TEXEL_MAX)) return null;
  const x0 = Math.floor(bb.min.x) - 1, z0 = Math.floor(bb.min.z) - 1;
  const W = Math.ceil((bb.max.x + 1 - x0) / t), H = Math.ceil((bb.max.z + 1 - z0) / t);
  const id = `${key}|${x0},${z0},${W},${H},${t}`;
  let e = BAKES.get(id);
  if (!e) {
    const B = new THREE.Vector4(x0, z0, 1 / (W * t), 1 / (H * t));
    // the quad's uv -> world xz (texel centres at x0 + (i + 0.5) t); sampled back with the same uv convention
    const node = new THREE.RTTNode(fn(uv().mul(vec2(W * t, H * t)).add(vec2(x0, z0))), W, H, {
      type: THREE.UnsignedByteType, depthBuffer: false, generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, autoUpdate: false,
    });
    node.renderTarget.texture.anisotropy = 8; // grazing views keep the distant drifts (as the per-pixel noise did)
    node.renderTarget.texture.colorSpace = THREE.NoColorSpace;
    node.name = 'snowRelief'; // (a shader identifier: the texture's binding name)
    BAKES.set(id, (e = { node, B, drawn: true }));
    // (three calls it once a frame that draws a material reading the relief: releaseBakes). Three marks the texture done
    // before it draws, and skips the draw while the quad's pipeline compiles (a dry picture only starts it): with
    // autoUpdate off nothing would draw it again, the drifts reading an empty texture for the visit. So a skipped draw stays to do.
    // VIS-267 (iPhone, iOS 27.3): drawn inside a compile (three updates the ground's nodes in the warm start's, and in a
    // covered prewarm), it is drawn dry (gpu/pipelines.js dryDraw): a plain draw there took three's sync pipeline, 0.45-1.17 s
    // of Safari's one work queue at ~4.5 s, every async pipeline's next step behind it, + 0.08-0.57 s at its first draw.
    // Dry, its pipeline compiles beside the others (the compile waits for it) and the next frame bakes the same bytes.
    // In a frame (a relief made on the live map: another snow pattern in Fine-tune, a tour's season cut) it bakes at
    // once, as before: the ground never reads a relief not yet baked there.
    node.updateBefore = function (frame) {
      e.drawn = true;
      const r = frame.renderer, draw = () => Object.getPrototypeOf(this).updateBefore.call(this, frame);
      if (!drewAll(r, isPreCompiling(r) ? () => dryDraw(r, draw) : draw)) this.textureNeedsUpdate = true;
    };
  }
  // the RTT node itself sits in the graph (its updateBefore draws the texture once); its uv = this pixel's world xz
  e.node.uvNode = gp.sub(vec2(e.B.x, e.B.y)).mul(vec2(e.B.z, e.B.w));
  return e.node;
}
// VIS-267: a kept relief only while a season that draws it shows (as gpu/seasonmaps.js does for the control maps): main.js
// marks the bakes when a season switch has put the new season's materials in (markBakes), and 2 s later frees on the
// graphics chip each one no frame has drawn since (releaseBakes: winter's snow relief while summer shows, Aurora 6.7 MB).
// Its node and the materials stay; the next frame that draws one bakes it again into the same target, by the same
// shader from the same numbers: the same bytes, mips included.
/** Every kept relief counts as not drawn until a frame draws it. */
export function markBakes() { for (const e of BAKES.values()) e.drawn = false; }
/** Frees the kept reliefs no frame drew since markBakes(); -> how many. */
export function releaseBakes() {
  let n = 0;
  for (const e of BAKES.values()) {
    if (e.drawn || e.node.textureNeedsUpdate) continue; // (drawn, or not baked yet / already freed)
    e.node.renderTarget.dispose(); e.node.textureNeedsUpdate = true; n++;
  }
  return n;
}
// ---- WS-78 lane G (end)
/** WS-16 drifts (albedo + normal, 6-30 m) and wind ripples (0.6-1.2 m, near the camera only) in world space, as a
 *  relief function ({ gp: world xz, gd: view distance, u, b: the node builder }) -> { gn: world normal (unnormalised),
 *  d0: 0..1 crest }. The default numbers are today's; lazy/groundlook.js uses it with the map's own
 *  (ground.look.snow.relief). WS-78 lane G: with the builder of a ground mesh, the drifts (and the ripples' patch mask)
 *  come from a texture made once (bakedAt); without it, per pixel as before. */
export function snowRipples(R = { scale: 22, gain: 2.4, wave: 0.85, fade: [60, 140] }) {
  const k = 22 / R.scale, e = 0.9 / k;
  // drifts: two scales of smooth relief; brightness follows (crests catch light, hollows hold blue)
  const D = (q) => grdFbm(q.mul(0.045 * k)).mul(0.7).add(grdNoise(q.mul(0.16 * k)).mul(0.3));
  const M = (q) => grdNoise(q.mul(0.08).add(3.0)); // the ripples' patches (12.5 m cells)
  const bake = (q) => { const d = D(q).toVar(); return vec4(d, D(q.add(vec2(e, 0.0))).sub(d).mul(BAKE_SLOPE).add(0.5), D(q.add(vec2(0.0, e))).sub(d).mul(BAKE_SLOPE).add(0.5), M(q)); };
  return ({ gp, gd, u, b }) => {
    const T = bakedAt(b, `ripples:${k}`, bake, gp);
    let d0, sx, sz, mk;
    if (T) {
      const t = vec4(T).toVar(); // (one tap, at the top level: implicit derivatives need uniform control flow)
      d0 = t.r; sx = t.g.sub(0.5).div(BAKE_SLOPE); sz = t.b.sub(0.5).div(BAKE_SLOPE); mk = () => t.a;
    } else {
      d0 = D(gp).toVar(); sx = D(gp.add(vec2(e, 0.0))).sub(d0); sz = D(gp.add(vec2(0.0, e))).sub(d0); mk = () => M(gp);
    }
    const gx = sx.mul(-R.gain).mul(u.uGrdDrift), gz = sz.mul(-R.gain).mul(u.uGrdDrift);
    // wind ripples (sastrugi): crests across the wind, broken up; fade out beyond ~140 m (sub-pixel)
    const wd = u.uGrdWind;
    const rip = float(0.0).toVar();
    If(gd.lessThan(R.fade[1]), () => { // WS-77: `near` is exactly 0 from fade[1] on: no ripple noise there
      const near = smoothstep(R.fade[0], R.fade[1], gd).oneMinus();
      const along = dot(gp, wd).add(grdNoise(gp.mul(0.35)).mul(2.2));
      const mask = smoothstep(0.35, 0.75, mk());
      rip.assign(along.mul(6.2831 / R.wave).cos().mul(mask).mul(near).mul(u.uGrdRipple).mul(0.22).mul(u.uGrdRipSun ?? 1)); // WS-82: x the low-sun share (setSnowSun)
    });
    return { gn: vec3(gx.add(wd.x.mul(rip)), 1.0, gz.add(wd.y.mul(rip))), d0 };
  };
}
const SNOW = new WeakMap();
// ---- WS-82 ripples under a low sun (begin): ground.look.snow.lowSun = { from, to, swing } (absent = today's ripples).
// The ripples tilt the normal along the wind by up to a = ripples x 0.22 (~6 deg). Sunlight on a flat snowfield is
// L.y; across a crest it swings by about a x |L_h . wind| / L.y of it: under a 7 deg sun that is +-85 %, and the
// crests read as zebra bands in every sunlit gap between tree shadows. Under a low sun the ripples are scaled so that
// swing stays under `swing` (0.25 = +-25 %), and back to full from `from` to `to` deg of sun (smoothstep): from `to` up
// the ground is today's. A uniform per material, set when the sun moves (main.js applyTime): no rebuild.
const RIP_SUN = new Set(); // { u, cfg } of every snow material with a lowSun look
/** Sets the ripples' low-sun share on every snow material from the sun's direction (world, unit vector). */
export function setSnowSun(dir) {
  const ly = Math.max(dir.y, 1e-3), alt = Math.asin(Math.min(1, ly)) * 180 / Math.PI;
  for (const { u, cfg } of RIP_SUN) {
    const L = cfg === true ? {} : cfg, from = L.from ?? 4, to = L.to ?? 20, swing = L.swing ?? 0.25;
    const a = u.uGrdRipple.value * 0.22, w = u.uGrdWind.value, h = Math.abs(dir.x * w.x + dir.z * w.y) * a;
    const cap = h > 1e-6 ? Math.min(1, (swing * ly) / h) : 1, t = THREE.MathUtils.smoothstep(alt, from, to);
    u.uGrdRipSun.value = t >= 1 ? 1 : cap + (1 - cap) * t; // exactly 1 from `to` up (today's pixels)
  }
}
// ---- WS-82 ripples under a low sun (end)
/** The snow look state of a material patched by patchSnow: { u, relief, albedo, drift } (read at build time;
 *  lazy/groundlook.js replaces relief / albedo, then sets material.needsUpdate). */
export const snowState = (m) => SNOW.get(m);
/** Drifts (albedo + normal, 6-30 m) and wind ripples (0.6-1.2 m, near the camera only) in world space. */
export function patchSnow(material, look) {
  // VIS-267 (rescue night): set while the map runs (the hour: setSnowSun; the look editor: lazy/groundlook.js), so in the
  // shared render group: a material's own group is uploaded only when three sees the object change (never on the rescue set)
  const live = (v) => uniform(v).setGroup(renderGroup);
  const u = {
    uGrdDrift: live(look.drifts), uGrdRipple: live(look.ripples),
    uGrdWind: live(new THREE.Vector2(...look.wind).normalize()),
    uGrdRipSun: live(1), // WS-82: the ripples' share at this sun (setSnowSun; 1 = today's)
  };
  if (look.lowSun) RIP_SUN.add({ u, cfg: look.lowSun }); // WS-82
  const S = { u, relief: snowRipples(), albedo: 0.07, drift: true };
  const gnP = property('vec3', pname('grdN'));
  onColor(material, (b) => {
    const gp = positionWorld.xz.toVar(), gd = length(positionView).toVar();
    const { gn, d0 } = S.relief({ gp, gd, u, b }); // WS-78 lane G: b = the builder (its mesh's extent for the kept relief)
    gnP.assign(gn);
    setRGB(diffuseColor.rgb.mul(d0.sub(0.5).mul(S.albedo).mul(S.drift ? u.uGrdDrift : 1.0).add(1.0)));
  }, 40);
  onNormal(material, (nv) => tiltNormal(nv, gnP), 20);
  material.userData.groundLook = u;
  SNOW.set(material, S);
  HOOK.snow?.(material, look); // WS-63 ground: snow pattern, grain, glints, glow
  return u;
}

// ------------------------------------------------------------------ VIS-267 W9: the ground's place in the draw order
/** renderOrder of every opaque ground mesh (main.js: terrain-core, landscape-far, the apron, zone-terrain-*): after
 *  every other opaque thing (0: buildings, props, the cut-out trees' alpha-to-coverage draws, which keep their place
 *  after the other opaque ones), before the ribbons' depth-only parts (1) and the sky (1e9). The ground is the one
 *  heavy shader per pixel on phones and covers the frame; drawn last of the opaque things, the depth of the trees and
 *  buildings in front of it is already there, so the pixels they hide are never shaded (owner's iPhone 13 Pro run,
 *  2026-10-23: -2 ms of 27). Opaque draws do not blend; only where a thing meets the ground at exactly the same depth
 *  (a trunk's foot far away, a hut's base) does the later draw win the pixel, so a few contact pixels change. */
export const GROUND_ORDER = 0.5;

// ---- VIS-267 ground picture (begin, owner's pick): the core ground picture as brightness (full size) + colour (half
// size) in one R8 texture (groundpaint.js toLite / liteAtlas): 1.5 bytes a texel instead of 4 (+ mips: Aurora 25 MB
// instead of 63), and one texture like the RGBA picture it replaces (a fragment stage reads at most 16 on many
// adapters). Every repaint (season, drawn paths, Fine-tune) fills the same texture; the CPU copy goes once uploaded.
/** The texture for a first paint ({ w, h, aw, ah, gap, a }); anisotropy as the RGBA picture's. */
export function liteTextures(lite, anisotropy = 1) {
  const t = new THREE.DataTexture(lite.a, lite.aw, lite.ah, THREE.RedFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.minFilter = THREE.LinearMipmapLinearFilter; t.magFilter = THREE.LinearFilter; t.generateMipmaps = true; t.anisotropy = anisotropy;
  t.unpackAlignment = 1; // rows of aw bytes, unpadded: aw is only even (WebGL2 refuses the upload where aw % 4 = 2)
  const T = { t, lite: { w: lite.w, h: lite.h, aw: lite.aw, ah: lite.ah, gap: lite.gap } };
  setLite(T, lite);
  return T;
}
/** A new paint into the same texture (same size: the extent and texels per metre never change on a page). */
export function setLite(T, lite) {
  const t = T.t;
  t.image = { data: lite.a, width: t.image.width, height: t.image.height };
  t.onUpdate = () => { t.onUpdate = null; t.image = { data: null, width: t.image.width, height: t.image.height }; }; // the CPU copy (Aurora 19 MB) goes once uploaded
  t.needsUpdate = true;
}
/** The colour node (vec4, linear): brightness and the two colour planes from the one texture (three reads at the
 *  same ground position), YCoCg (groundpaint.js toLite's scale) -> the canvas's sRGB -> linear, times the material's
 *  colour as `map` was (a colorNode replaces color x map; main.js setSeason tints the ground by it). */
export function liteColor(T) {
  const { w, h, aw, ah, gap } = T.lite, w2 = Math.ceil(w / 2), h2 = Math.ceil(h / 2), p = uv();
  const y = texture(T.t, p.mul(vec2(w / aw, h / ah))).r;
  const cv = p.y.mul(h2 / ah).add((h + gap) / ah);
  const co = texture(T.t, vec2(p.x.mul(w2 / aw), cv)).r.sub(128 / 255);
  const cg = texture(T.t, vec2(p.x.mul(w2 / aw).add((w2 + gap) / aw), cv)).r.sub(128 / 255), t = y.sub(cg);
  return vec4(sRGBTransferEOTF(clamp(vec3(t.add(co), y.add(cg), t.sub(co)), 0.0, 1.0)).mul(materialColor), 1.0);
}
// ---- VIS-267 ground picture (end)

// ------------------------------------------------------------------ ribbons: roads, paths, trails
/** Bounding box [x0, z0, x1, z1] of a polygon, grown by m. */
function polyBox(poly, m = 0) {
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (const [x, z] of poly) { x0 = Math.min(x0, x); z0 = Math.min(z0, z); x1 = Math.max(x1, x); z1 = Math.max(z1, z); }
  return [x0 - m, z0 - m, x1 + m, z1 + m];
}
/** Signed distance to a polygon's outline: positive inside, negative outside (metres). */
export function signedDist(poly, x, z) {
  const d = lineDist(x, z, poly, true); // VIS-197: the shared sums (lines/measure.js)
  return inRing(x, z, poly) ? d : -d;
}
/** WS-88: areaDist, kept per area and grid point (the ribbons' area grids ask the same points at every build of the
 *  lines: a drag's end built every ploughed lot's distances again). Only for grid points (a bounded set). */
const distKept = new WeakMap();
export function areaDistAt(a, x, z) {
  let m = distKept.get(a);
  if (!m) distKept.set(a, (m = new Map()));
  let row = m.get(x);
  if (!row) m.set(x, (row = new Map()));
  let d = row.get(z);
  if (d === undefined) row.set(z, (d = areaDist(a, x, z)));
  return d;
}
/** WS-88: three's computeVertexNormals for an indexed geometry, on the arrays (the same sums in the same order, so the
 *  same normals; ~5x faster, which a drag's preview ribbon needs every frame). */
function vertexNormals(g) {
  const P = g.attributes.position.array, I = g.index.array, N = new Float32Array(P.length);
  for (let i = 0; i < I.length; i += 3) {
    const a = I[i] * 3, b = I[i + 1] * 3, c = I[i + 2] * 3;
    const cbx = P[c] - P[b], cby = P[c + 1] - P[b + 1], cbz = P[c + 2] - P[b + 2], abx = P[a] - P[b], aby = P[a + 1] - P[b + 1], abz = P[a + 2] - P[b + 2];
    const nx = cby * abz - cbz * aby, ny = cbz * abx - cbx * abz, nz = cbx * aby - cby * abx;
    N[a] += nx; N[a + 1] += ny; N[a + 2] += nz; N[b] += nx; N[b + 1] += ny; N[b + 2] += nz; N[c] += nx; N[c + 1] += ny; N[c + 2] += nz;
  }
  for (let i = 0; i < N.length; i += 3) { const l = Math.sqrt(N[i] * N[i] + N[i + 1] * N[i + 1] + N[i + 2] * N[i + 2]) || 1, s = 1 / l; N[i] *= s; N[i + 1] *= s; N[i + 2] *= s; }
  g.setAttribute('normal', new THREE.BufferAttribute(N, 3));
}
/** Signed distance to an area { polygon, holes }: positive inside the polygon and outside every hole. */
export function areaDist(a, x, z) {
  let d = signedDist(a.polygon, x, z);
  for (const h of a.holes || []) d = Math.min(d, -signedDist(h, x, z));
  return d;
}
/** Segment grid for "distance to the nearest other line" queries. (WS-88: exported: main.js previewRibbons's other lines;
 *  numbers for cell keys, the same cells) */
export function segGrid(lines, cell = 12) {
  const map = new Map(), K = (x, z) => x * 65536 + z, NONE = [];
  lines.forEach((ln, li) => {
    for (let i = 1; i < ln.pts.length; i++) {
      const [ax, az] = ln.pts[i - 1], [bx, bz] = ln.pts[i];
      const x0 = Math.floor(Math.min(ax, bx) / cell), x1 = Math.floor(Math.max(ax, bx) / cell), z0 = Math.floor(Math.min(az, bz) / cell), z1 = Math.floor(Math.max(az, bz) / cell);
      for (let x = x0; x <= x1; x++) for (let z = z0; z <= z1; z++) { const k = K(x, z); let l = map.get(k); if (!l) map.set(k, (l = [])); l.push([li, ax, az, bx, bz]); }
    }
  });
  return (x, z, skip, pick) => { // -> min over other lines of (distance - their half width); `pick(line)` = only those
    let best = Infinity;
    const cx = Math.floor(x / cell), cz = Math.floor(z / cell);
    for (let a = cx - 1; a <= cx + 1; a++) for (let b = cz - 1; b <= cz + 1; b++) {
      for (const [li, ax, az, bx, bz] of map.get(K(a, b)) || NONE) {
        if (li === skip || (pick && !pick(lines[li]))) continue;
        best = Math.min(best, stretchDist(x, z, ax, az, bx, bz) - lines[li].half); // VIS-219: the shared sum
      }
    }
    return best;
  };
}

/** How hard each ribbon kind's surface is (index = kind): where two lines overlap the harder one shows and the softer
 *  sinks into it, as on the ground (a ski trail or a snowmobile route crossing a ploughed road is road there). Road 4
 *  (ploughed to the hard pack; ploughed areas too), vehicle track 3, snowmobile 2, groomed ski trail 1, trodden path 0;
 *  (5, the sledding hill, is an area: drawn first) an ice lane 3 (swept ice: a trail crossing it is ice there, a road wins). */
export const HARD = [4, 0, 1, 2, 3, 0, 3];
/** A path drawn in Fine-tune (resort.drawnRoads: road, path, trail, ski, snowmobile, ice; never a walking line, kind 'guide', which is not drawn) as the ribbons of season s draw
 *  it: [list in the scene data, entry]. A road is a road in every season (ploughed in winter, gravel outside it); in
 *  winter a ski trail is groomed, a snowmobile route has its tread, an ice path is an ice lane (paths, kind 'ice'), a
 *  trail and a walking path are trodden snow; outside winter all but a road are earth paths. (main.js withDrawn,
 *  lazy/ribbondrag.js) */
export function drawnWay(p, winter) {
  const list = p.kind === 'road' ? 'roads' : winter && (p.kind === 'ski' || p.kind === 'snowmobile') ? 'trails' : 'paths';
  return [list, { kind: p.kind === 'ice' && !winter ? 'path' : p.kind, pts: p.pts, width: p.width, drawn: true, id: p.id }];
}
/** (owner 2026-10-31) A road, path or trail of the map whose kind was changed in Fine-tune (`as`, lazy/ways.js applyWays)
 *  as the ribbons of season s draw it: [list, entry], seasons as drawnWay's. It keeps its name and line; its width is the
 *  new kind's (the look's road or path width, a trail's own); a road keeps its road class when it was one. */
const ROAD_CLASS = /^(motorway|trunk|primary|secondary|tertiary|unclassified|residential|service|living_street)/;
export function wayAs(e, winter) {
  const { width: _w, as, ...o } = e, [list] = drawnWay({ kind: as }, winter);
  return [list, { ...o, as, kind: as === 'road' ? (ROAD_CLASS.test(o.kind || '') ? o.kind : 'service') : as === 'ice' && !winter ? 'path' : as }];
}
/** The ways `d` ({ roads, paths, trails }) with each one whose kind was changed (`as`) in its new kind's list (wayAs);
 *  `d` itself when there is none (main.js withDrawn, lazy/ribbondrag.js). */
export function waysAs(d, winter) {
  if (![d.roads, d.paths, d.trails].some((l) => l.some((r) => r.as))) return d;
  const o = { roads: [], paths: [], trails: [] };
  for (const l in o) for (const r of d[l]) { const [k, e] = r.as ? wayAs(r, winter) : [l, r]; o[k].push(e); }
  return { ...d, ...o };
}

/**
 * One ribbon mesh for every road, path and trail in scene.json, draped on the terrain.
 * kinds: 0 road (banks + tyre tracks), 1 footpath (trodden), 2 ski trail, 3 snowmobile trail, 4 track (path kind 'track'),
 * 6 ice lane (path kind 'ice', winter only: drawnWay); 5 is lazy/groundlook.js's sledding hill
 * Overlaps follow HARD: a softer line fades out over the harder one's lane (and inside a ploughed area), and a road's
 * bank opens for a softer line only where that line crosses it, not where it runs along the road.
 */
export function buildRibbons(data, heightAt, look, addSnowShading, opts = {}) {
  const lines = [];
  // WS-58: a path drawn by hand carries its own `width`, and its trail is drawn even where the look has no trails (`drawn`)
  // (WS-88: `src` = the entry each line was made from: main.js previewRibbons folds a line's rows away by it)
  for (const r of data.roads) lines.push({ pts: r.pts, kind: 0, half: (r.width ?? look.roadWidth[r.kind] ?? look.roadWidth.default) / 2, src: r });
  for (const p of data.paths) lines.push({ pts: p.pts, kind: p.kind === 'track' ? 4 : p.kind === 'ice' ? 6 : 1, half: (p.width ?? (p.kind === 'track' ? look.trackWidth : look.pathWidth)) / 2, src: p });
  for (const t of data.trails) if (look.trails || t.drawn || t.as) lines.push({ pts: t.pts, kind: t.kind === 'ski' ? 2 : 3, half: t.width ? t.width / 2 : t.kind === 'ski' ? 1.6 : 0.8, src: t });
  // WS-88 `opts`: `mat` = the ribbon material of an earlier build of the same look, used again (no new node material: no
  // shader build on WebGPU); `near` (a preview of some lines, main.js previewRibbons) = segGrid of the lines NOT in
  // `data` (where these lines' banks open), and no ploughed-area grid of its own (the big mesh keeps those)
  const BH = look.bankH, BW = look.bankW, BS = look.bank || bankShape(), BCOL = bankColumns(BW, BS), NB = BCOL.length; // VIS-228: the banks' shape
  // VIS-228: each line as it is drawn: hooks and zig-zags of a hand-drawn line taken out, corners rounded so the band
  // (the lane and its banks) never folds on the inside of a bend (lines/measure.js bandLine); `ln.pts` stays the entry's.
  // A path drawn by hand in Fine-tune (`drawn`) is eased as wide as the band reaches: its clicks never show as corners.
  for (const ln of lines) { const reach = ln.half + (ln.kind === 0 ? BW : 0.5); ln.band = ln.pts.length > 1 ? bandLine(ln.pts, reach, reach, 1, ln.src?.drawn ? reach : 0) : ln.pts; }
  const only = typeof opts.near === 'function', grid = only ? opts.near : segGrid(lines.map((ln) => ({ pts: ln.band, half: ln.half, kind: ln.kind })));
  const near = (x, z, li, pick) => grid(x, z, only ? -1 : li, pick); // (`pick`: only lines it keeps; ground.js segGrid)
  const P = [], A = [], idx = [], E = [], S = []; // VIS-228: E = per vertex, how much of the line shows (1; less at its ends); S = 1 where it surely shows whole
  // VIS-228: where a line is surely opaque (its shading below gives alpha 1 whatever the noise): the lane inside its soft
  // edge (ground.look.roads.finish.edge, groundlook), a road's banks inside their foot, the middle of a path or trail.
  // Only those parts write depth (the child mesh 'ground-ribbons-depth'): a bank hides what lies behind it, and nothing
  // see-through can hide another line (the cut-outs at junctions).
  const fe = look.x?.roads?.finish, edgeIn = Math.max(0.35, (fe && typeof fe === 'object' ? fe.edge : 0.35) ?? 0.35), coreIn = edgeIn + 0.4 * Math.min(1, edgeIn / 0.35) + 0.1;
  const solidAt = (kind, a, h, bank, show) => show > 0.999 && (kind === 0 ? a <= h - coreIn || (bank >= 0.2 && a <= h + BW - BS.fade - 0.2)
    : kind === 2 ? a <= h - (look.ski?.edge ?? 0.15) - 0.08 : kind === 3 ? a <= 0.6 * h - 0.25 : kind === 6 ? a <= 0.85 * h - 0.15 : a <= 0.35 * h - 0.29);
  // R1-ROADS: ploughed areas (resort.paint[] with surface 'ploughed': a parking lot, a farm yard): the road surface
  // over the whole polygon, plough banks round its edge, open where a road comes in; roads crossing it lose their banks
  const areas = (data.ploughed || []).filter((a) => a.polygon?.length > 2).map((a) => ({ area: a, box: polyBox(a.polygon, BW + 0.5) }));
  const inArea = (x, z, m = 0.5) => areas.some((a) => x > a.box[0] && x < a.box[2] && z > a.box[1] && z < a.box[3] && areaDist(a.area, x, z) > -m);
  const grids = only ? [] : areas; // WS-88: a preview draws no area of its own
  // how much of a line of hardness r shows at x, z: nothing on a harder line's lane or inside a ploughed area (the same
  // hard pack as a road), all of it from 1 m beyond their edge
  const cover = (x, z, li, r) => {
    let c = THREE.MathUtils.smoothstep(near(x, z, li, (o) => HARD[o.kind] > r), 0, 1);
    for (const a of areas) if (c > 0 && x > a.box[0] && x < a.box[2] && z > a.box[1] && z < a.box[3]) c *= THREE.MathUtils.smoothstep(-areaDist(a.area, x, z), 0, 1);
    return c;
  };
  lines.forEach((ln, li) => {
    const c = ln.band;
    if (c.length < 2) return;
    const w = ln.half, n = c.length, loop = n > 3 && c[0][0] === c[n - 1][0] && c[0][1] === c[n - 1][1];
    // VIS-228 line ends: a free end runs on a little and fades out over F m around its point (no square end); an end
    // that joins another line (on it or within 0.5 m of its edge) runs 1 m on into it and fades there, so two lines
    // that continue each other overlap instead of leaving a seam. A loop has no ends.
    const F = Math.min(4, Math.max(1.2, w * 1.2));
    const endOf = (e, f) => {
      const dx = e[0] - f[0], dz = e[1] - f[1], L = Math.hypot(dx, dz) || 1, joined = near(e[0], e[1], li) < 0.5, ext = joined ? 1 : F / 2;
      return { joined, ext, p: [e[0] + (dx / L) * ext, e[1] + (dz / L) * ext] };
    };
    const e0 = loop ? null : endOf(c[0], c[1]), e1 = loop ? null : endOf(c[n - 1], c[n - 2]);
    const st = ln.kind === 0 ? 1.5 : 1.2;
    // (a row on each end point itself: where two lines continue each other, each shows whole up to the point)
    const pts = loop ? resampleEven(c, st) : [...resampleEven([e0.p, c[0]], 0.5).slice(0, -1), ...resampleEven(c, st), ...resampleEven([c[n - 1], e1.p], 0.5).slice(1)];
    let total = 0;
    for (let i = 1; i < pts.length; i++) total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    const endShow = (s, e) => (e.joined ? THREE.MathUtils.smoothstep(s, -e.ext, 0) : THREE.MathUtils.smoothstep(s, -F / 2, F / 2)); // s = m in from the end
    // cross-section: [offset m from the centre, lift above the ground, bank weight] (VIS-228: the banks' columns from
    // ground.look.roads.bank, bankColumns; the foot (last) column carries no bank weight)
    const prof = ln.kind === 0
      ? [...BCOL.map(([e, up], j) => [-(w + e), up, j === BCOL.length - 1 ? 0 : 1]).reverse(), [-w, 0, 0], [-w * 0.55, 0, 0], [0, 0, 0], [w * 0.55, 0, 0], [w, 0, 0], ...BCOL.map(([e, up], j) => [w + e, up, j === BCOL.length - 1 ? 0 : 1])]
      : [[-(w + 0.5), 0, 0], [-w, 0, 0], [0, 0, 0], [w, 0, 0], [w + 0.5, 0, 0]];
    ln.prof = prof; // (lazy/groundlook.js irregularBanks: which columns are bank, crest, foot)
    const lift = ln.kind === 0 ? 0.06 : 0.045, hard = HARD[ln.kind];
    let along = 0;
    const base = P.length / 3, nP = prof.length, lx = new Float64Array(nP), lz = new Float64Array(nP);
    const last = pts.length - 1;
    for (let i = 0; i < pts.length; i++) {
      // (a loop's first and last rows are the same row: the way on through the join on both sides of it, the last row's
      // vertices put on the first's. Each with its own one-sided direction left a wedge of snow at the join)
      const [x, z] = pts[i], [px, pz] = pts[loop && !i ? last - 1 : Math.max(0, i - 1)], [qx, qz] = pts[loop && i === last ? 1 : Math.min(last, i + 1)];
      if (i) along += Math.hypot(x - pts[i - 1][0], z - pts[i - 1][1]);
      let dx = qx - px, dz = qz - pz; const L = Math.hypot(dx, dz) || 1; dx /= L; dz /= L;
      const nx = -dz, nz = dx; // left normal
      const s0 = loop ? 9 : along - e0.ext, s1 = loop ? 9 : total - e1.ext - along; // m from each end (negative: past it)
      const show = loop ? 1 : endShow(s0, e0) * endShow(s1, e1);
      const endFade = Math.min(1, Math.max(0, s0) / 6) * Math.min(1, Math.max(0, s1) / 6); // banks taper off over the last 6 m of an end
      for (let k = 0; k < nP; k++) {
        const [off, up, bw] = prof[k];
        let vx = x + nx * off, vz = z + nz * off;
        if (i && (vx - lx[k]) * dx + (vz - lz[k]) * dz < 0) { vx = lx[k]; vz = lz[k]; } // VIS-228: inside a bend tighter than the band, wait (no fold)
        if (loop && i === last) { vx = P[(base + k) * 3]; vz = P[(base + k) * 3 + 2]; }
        lx[k] = vx; lz[k] = vz;
        // (VIS-228: the lane's edge column carries the weight of the bank beside it: shading only, no height. At 0 it
        // made the lane's edge see-through under every bank: a pale line along the bank's foot. Also with no bank height
        // (a look with banks: 0, Aurora's summer and autumn): a hard see-through strip along both lane edges)
        const edge = ln.kind === 0 && (k === NB || k === nP - 1 - NB);
        let bank = bw || (edge ? 1 : 0);
        if (bank > 0) { // another road/path here: open the bank (VIS-228: over 4.5 m, was 2.6: no blunt bank end at a junction)
          const d = near(vx, vz, li, (o) => HARD[o.kind] >= hard), soft = near(vx, vz, li, (o) => HARD[o.kind] < hard);
          bank *= THREE.MathUtils.smoothstep(d, 0.3, 4.5) * THREE.MathUtils.smoothstep(soft, 0, 1) * endFade; // (a softer line: only where it crosses the bank, a cut as wide as it plus 1 m a side; one running along the lane leaves the bank whole)
          if (areas.length && inArea(vx, vz)) bank = 0; // R1-ROADS: inside a ploughed area there is no bank
        }
        // VIS-228: the outer edge and a line's end row (nothing shows there) just under the ground: a mesh edge hovering
        // above it cast a hard shadow line
        const y = k === 0 || k === nP - 1 || (!loop && (i === 0 || i === pts.length - 1)) ? heightAt(vx, vz) - 0.03 : heightAt(vx, vz) + lift + (ln.kind === 0 ? up * BH * bank : 0) - (bw > 0 ? 0.015 * Math.min(1, Math.max(0, 1 - bank * 2)) : 0); // (VIS-228: the dip eased in, no 1.5 cm step at 0.5)
        const sv = hard < 4 && show > 0 ? show * cover(vx, vz, li, hard) : show; // sunk into a harder line here
        P.push(vx, y, vz);
        A.push(off, along, ln.kind, w, bank);
        E.push(sv); S.push(solidAt(ln.kind, Math.abs(off), w, bank, sv) ? 1 : 0);
      }
    }
    const nRow = pts.length;
    ln.at = [base, nRow, nP]; // WS-63 ground: where the line's rows are (HOOK.rib)
    for (let i = 0; i < nRow - 1; i++) for (let k = 0; k < nP - 1; k++) {
      const a = base + i * nP + k, b = a + 1, c = a + nP, d = c + 1;
      idx.push(a, b, c, b, d, c); // counter-clockwise from above (faces up)
    }
  });
  // R1-ROADS: each ploughed area = a draped grid (1.5 m) over its box, cells kept where the area or its bank reaches.
  // Shaded as a road whose centreline is far away (aHalf = AH, aAcross = AH - inside distance): the inside is the
  // compacted road surface, the outside BW m is the plough bank (raised, opened where a road comes in), then fades.
  const AH = 500, step = 1.5;
  for (const ar of grids) {
    const [x0, z0, x1, z1] = ar.box, nx = Math.ceil((x1 - x0) / step) + 1, nz = Math.ceil((z1 - z0) / step) + 1;
    const base = P.length / 3, sd = new Float32Array(nx * nz);
    ar.at = [base, nx, nz]; // WS-63 ground
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
      const x = x0 + i * step, z = z0 + j * step, d = areaDistAt(ar.area, x, z), e = -d; // (WS-88: kept)
      sd[j * nx + i] = d;
      const up = bankLift(e, BW, BS); // VIS-228: the same cross-section as the roads' banks
      let bank = e > 0 && e < BW ? 1 : 0;
      if (bank) bank *= THREE.MathUtils.smoothstep(near(x, z, -1), 0.3, 2.6); // a road comes in here: no bank
      P.push(x, heightAt(x, z) + 0.05 + up * BH * bank, z); // 1 cm under the road ribbons, which draw over it
      A.push(AH - Math.min(d, AH - 1), x * 0.71 + z * 0.29, 0, AH, bank);
    }
    for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nx - 1; i++) {
      const a = base + j * nx + i, b = a + 1, c = a + nx, d = c + 1;
      const k = j * nx + i;
      if (Math.max(sd[k], sd[k + 1], sd[k + nx], sd[k + nx + 1]) <= -BW) continue;
      idx.push(a, c, b, b, c, d); // faces up (x east, z south)
    }
  }
  const X = HOOK.rib?.({ P, A, idx, lines, areas: grids, look, heightAt, near, data, snow: !!addSnowShading, only }); // WS-63 ground: banks, sledding, lots (WS-88: + only)
  { // VIS-228: the areas (ploughed lots, a sledding hill: aHalf 500 and up) drawn first and the lines over them: with no
    // depth written by the see-through ribbons, the order decides, and a lot drawn last covered the roads across it
    // (and the lines from the softest to the hardest: a road's soft edge blends over the trail that sinks into it)
    const first = [], rest = [[], [], [], [], []];
    for (let i = 0; i < idx.length; i += 3) (A[idx[i] * 5 + 3] >= 450 ? first : rest[HARD[A[idx[i] * 5 + 2]] ?? 4]).push(idx[i], idx[i + 1], idx[i + 2]);
    let k = 0;
    for (const v of first) idx[k++] = v;
    for (const r of rest) for (const v of r) idx[k++] = v;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  const at = new Float32Array(A);
  const ib = new THREE.InterleavedBuffer(at, 5);
  g.setAttribute('aAcross', new THREE.InterleavedBufferAttribute(ib, 1, 0));
  g.setAttribute('aAlong', new THREE.InterleavedBufferAttribute(ib, 1, 1));
  g.setAttribute('aKind', new THREE.InterleavedBufferAttribute(ib, 1, 2));
  g.setAttribute('aHalf', new THREE.InterleavedBufferAttribute(ib, 1, 3));
  g.setAttribute('aBank', new THREE.InterleavedBufferAttribute(ib, 1, 4));
  const show = new Float32Array(P.length / 3).fill(1); show.set(E); // VIS-228: ends fade (areas, banks' strips, lots: 1)
  g.setAttribute('aEnd', new THREE.Float32BufferAttribute(show, 1));
  g.setIndex(idx);
  vertexNormals(g); // WS-88 (was g.computeVertexNormals())
  // VIS-228: the opaque parts again, depth only, drawn with the opaque things (before the ribbons blend): they hold the
  // depth (a bank hides the lane behind it). Same positions (one shared attribute: a drag's folded rows fold here too).
  // The shadow stays the whole ribbon's (a bank's shadow tapers with its ramp; the edges that hovered are now under the
  // ground). A preview of some lines has none.
  // Black snow banks (2026-10-23, owner, the reindeer ride's seat): it is drawn AFTER the ground (renderOrder 1, every
  // opaque ground mesh is GROUND_ORDER, the sky 1e9). three sorts opaque things by their bounding sphere's depth, so from about
  // half the low cameras it came first; the ground under its opaque parts then failed the depth test and nothing but
  // the ribbons' colour was left to fill them. Any pixel that colour missed (a ribbon program not drawn: over the
  // sampler limit, VIS-272, or still compiling) stayed the clear colour: black, saw-toothed banks that flickered as the
  // camera moved. Drawn after the ground, such a pixel shows the snow under it.
  const withDepth = (mesh) => {
    mesh.castShadow = !only;
    mesh.renderOrder = -0.1; // first of the see-through things on the ground: it draws without blending, so whatever lay under it before (smoke, steam, a light's glow) is wiped; at order 0 that swapped with the camera (a sprite sorts by the map's origin, a mesh by its centre)
    if (only) return mesh;
    const solid = [];
    for (let i = 0; i < idx.length; i += 3) if (S[idx[i]] && S[idx[i + 1]] && S[idx[i + 2]]) solid.push(idx[i], idx[i + 1], idx[i + 2]);
    mesh.add(depthChild(g, solid));
    return mesh;
  };
  if (opts.mat) { // WS-88: the material of an earlier build, as it is (its shading parts were set at that build)
    const mesh = new THREE.Mesh(g, opts.mat);
    Object.assign(mesh, { name: 'ground-ribbons', receiveShadow: true });
    Object.assign(mesh.userData, { ribbonU: opts.mat.userData.ribbonU, lines, hAt: heightAt });
    return withDepth(mesh);
  }
  const C = look.colors, col = (h) => new THREE.Color(h);
  const U = {
    uRoad: uniform(col(C.road)), uRut: uniform(col(C.rut)), uBank: uniform(col(C.bank)), uBankIn: uniform(col(C.bankInner)),
    uPath: uniform(col(C.path)), uTrail: uniform(col(C.trail)), uIce: uniform(col(C.ice || '#5f7f96')), uRuts: uniform(look.ruts), uGauge: uniform(look.gauge), uBankW: uniform(look.bankW),
    uBankFade: uniform((look.bank || bankShape()).fade), // VIS-228: the bank foot's fade (m)
    uSki: uniform(skiVec(look.ski || skiLook())), // the groomed ski trail: grooves, cord, tone, edge (skiLook)
  };
  let mat = new THREE.MeshStandardNodeMaterial({ color: 0xffffff, roughness: 0.88, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4,
    blending: THREE.NoBlending, alphaToCoverage: true });
  // VIS-228 depthWrite false: lines overlap at every junction and crossing; a soft (see-through) edge of the line drawn
  // first wrote depth and hid the lines drawn after it there (square cut-outs, notches, stepped patches). The depth is
  // the opaque parts' (withDepth above); the ribbons cover each other in drawing order.
  // VIS-267: the see-through edges draw by alpha to coverage (the share of the 4 MSAA samples a pixel's ribbon covers),
  // as the trees and lit windows do, not by blending. A graphics card's blend into the 4x half-float scene picture is not
  // bit-exact from one draw to the next (RTX 5080, D3D12 and Vulkan alike: ~20-100 road pixels a draw one half-float
  // step off, even at alpha 1; still without MSAA, without blending or on a 32-bit picture), so a still view never
  // compared equal and the map never rested with a road in view (rest.js). Every render set draws MSAA 4 (render.js);
  // the coverage is dithered (ribbonShading) so the edge keeps its soft ramp.
  if (addSnowShading) mat = addSnowShading(mat) || mat;
  const R = ribbonShading(mat, U);
  X?.(mat, R); // WS-63 ground: their shading (replaces parts of R before the first draw)
  const mesh = new THREE.Mesh(g, mat);
  mesh.name = 'ground-ribbons';
  mesh.receiveShadow = true;
  withDepth(mesh);
  mesh.userData.ribbonU = U; // WS-77: setRibbonLook (one ribbon mesh for the bare seasons when their roads are the same)
  mat.userData.ribbonU = U; mesh.userData.lines = lines; mesh.userData.hAt = heightAt; // WS-88: the material kept, the lines' rows (main.js previewRibbons)
  return mesh;
}
/** VIS-228: the opaque parts' depth-only mesh over the ribbons' own positions. VIS-267 round 2: made out here, so no
 *  function kept with it (its raycast, its dispose listener) holds buildRibbons' scope: that kept the build's number
 *  arrays (P, A, idx, E, S: ~9 MB of JS heap at Aurora, ~13.5 at Lumo) for the visit. */
const noRay = () => {};
function depthChild(g, solid) {
  const gd = new THREE.BufferGeometry();
  gd.setAttribute('position', g.getAttribute('position')); gd.setIndex(solid);
  g.addEventListener('dispose', () => gd.dispose());
  const dm = new THREE.Mesh(gd, depthMat());
  Object.assign(dm, { name: 'ground-ribbons-depth', castShadow: false, receiveShadow: false, renderOrder: 1, raycast: noRay });
  return dm;
}
/** VIS-267 round 2 (phone memory): a ribbon mesh's CPU arrays, once on the graphics chip (gpu/release.js), for the
 *  visitor's page (main.js: after its first draw and after a season's ribbons are shown). Nothing reads them there: no
 *  ray tests the ribbons, the shadow proxy read them at the build, light casting draws them (thin casters) and reads
 *  only the position's version; a rebuild (setDrawn, another season) makes new ones from the scene data. The depth
 *  child shares the position: the bounds of both first. -> bytes freed */
export function releaseRibbons(renderer, mesh) {
  if (!mesh?.isMesh) return 0;
  const parts = [mesh, ...mesh.children.filter((c) => c.isMesh)];
  for (const { geometry: g } of parts) { if (!g.boundingBox) g.computeBoundingBox(); if (!g.boundingSphere) g.computeBoundingSphere(); }
  let bytes = parts.reduce((b, m) => b + releaseCpu(renderer, m), 0);
  // the five per-vertex values (aAcross .. aBank) share one interleaved buffer, which releaseCpu leaves: the same stub
  const a = mesh.geometry.attributes.aAcross, ib = a?.data;
  if (ib?.isInterleavedBuffer && ib.usage === THREE.StaticDrawUsage && ib.array.length > ib.stride && uploaded(renderer, a)) {
    bytes += ib.array.byteLength - ib.stride * ib.array.BYTES_PER_ELEMENT;
    ib.array = new ib.array.constructor(ib.stride); // (count stays: a plain property; never sent again: no needsUpdate)
  }
  return bytes;
}
/** VIS-228: the depth-only material of the ribbons' opaque parts (one for every build: no shader build again), with
 *  the ribbon material's polygon offset so that both land on the same depth. */
let DEPTH_MAT = null;
const depthMat = () => (DEPTH_MAT ||= new THREE.MeshBasicNodeMaterial({ colorWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4 }));
/** WS-77 QA: the season values of the ground surfaces drawn now (the WS-41 state fingerprint, docs/qa/ws41/states.mjs;
 *  on WebGPU three keeps no uniform list per material), as plain numbers by mesh name: water (water.js uniforms),
 *  detail layers (core / far / ribbons), the ribbons' colours, the far canopy tone. */
export function seasonState(root) {
  const out = {}, names = ['terrain-core', 'landscape-far', 'ground-ribbons'];
  const shown = (o) => { for (let p = o; p; p = p.parent) if (!p.visible) return false; return true; };
  const val = (v) => (typeof v === 'number' ? v : v?.isColor ? [v.r, v.g, v.b] : v?.isTexture ? `tex ${v.image?.width}x${v.image?.height}` : v?.toArray ? v.toArray() : v ?? null);
  const pick = (u, keys) => Object.fromEntries(keys.filter((k) => u?.[k]).map((k) => [k, Array.isArray(u[k]) ? u[k].map((x) => val(x.value)) : val(u[k].value)]));
  root.traverse((o) => {
    if (!names.includes(o.name) || out[o.name] || !shown(o)) return;
    const m = o.material, D = m?.userData?.groundDetail;
    out[o.name] = {
      ...pick(m?.userData?.water, ['uWOpenAll', 'uWGate', 'uWDeep', 'uWIceShare', 'uWRipple']),
      ...pick(D, ['uGdP', 'uGdW0', 'uGdW1', 'uGdCol', 'uGdAcc', 'uGdAccA', 'uGdTex']), ...(D ? { uGdCtl: (D.ctlTex || []).filter(Boolean).length } : {}),
      ...pick(o.userData.ribbonU, ['uRoad', 'uRut', 'uBank', 'uBankIn', 'uPath', 'uTrail', 'uRuts', 'uGauge', 'uBankW']),
      ...pick(o.userData.rim, ['uTreeCol']),
    };
  });
  return out;
}
/** WS-77: the colour and tyre-track values of another season's look on a ribbon mesh of the same shape. */
export function setRibbonLook(mesh, look) {
  const U = mesh?.userData.ribbonU, C = look?.colors;
  if (!U || !C) return;
  U.uRoad.value.set(C.road); U.uRut.value.set(C.rut); U.uBank.value.set(C.bank); U.uBankIn.value.set(C.bankInner); U.uPath.value.set(C.path); U.uTrail.value.set(C.trail); if (C.ice) U.uIce?.value.set(C.ice);
  U.uRuts.value = look.ruts; U.uGauge.value = look.gauge; U.uBankW.value = look.bankW; U.uBankFade.value = (look.bank || bankShape()).fade;
  U.uSki.value.copy(skiVec(look.ski || skiLook()));
}
const skiVec = (k) => new THREE.Vector4(k.grooves, k.cord, k.tone, k.edge);

/** The default tyre tracks of a ploughed road: a pair, glazed and darker, wandering a little. */
function roadDefault({ U, vAlong, vAcross, fine }) {
  const g = U.uGauge.add(grdNoise(vec2(vAlong.mul(0.05), 1.0)).sub(0.5).mul(0.35));
  const tw = grdNoise(vec2(vAlong.mul(0.3), 5.0)).mul(0.08).add(0.2).toVar();
  const rut = smoothstep(tw.mul(0.4), tw, abs(abs(vAcross).sub(g))).oneMinus().mul(grdNoise(vec2(vAlong.mul(0.6), vAcross.mul(4.0))).mul(0.45).add(0.55));
  return mix(U.uRoad.mul(fine.mul(0.06).add(0.97)), U.uRut, rut.mul(U.uRuts).mul(fine.mul(0.35).add(0.65)));
}
/** An ice lane's reflectance at normal incidence (clear ice ~0.02; raised so the sheen reads from the map's heights) and
 *  its extra sky reflection (the sky is dimmer than sunlit snow: without it the ice reads as a shadow) */
const ICE_F0 = 0.1, ICE_SKY = 2.2;
/** WS-16 ribbon surfaces (kinds: 0 road, 1 footpath, 2 ski trail, 3 snowmobile trail, 4 track, 6 ice lane) as hooks on the ribbon
 *  material. Returns R, the parts lazy/groundlook.js may replace before the first draw (WS-63 ground):
 *  head[](c) first (its own values), fine(c) the tone noise, edge(c) a road's surface share, road(c) a road's lane
 *  colour, extra[](c) after the surfaces (assign c.col / c.alpha), rough(r, c) the roughness.
 *  c = { U, vAcross, vAlong, vKind, vHalf, vBank, vRibW, k, a, h, wob, fine, col, alpha, surf (roads) }. */
function ribbonShading(mat, U) {
  const R = { head: [], fine: null, edge: null, road: null, extra: [], rough: null };
  const ice = property('float', pname('ribIce')); // how glossy the ice of an ice lane is here (0 elsewhere): the roughness and reflectance stages
  const attrs = () => ({
    U, vAcross: attribute('aAcross', 'float'), vAlong: attribute('aAlong', 'float'), vKind: attribute('aKind', 'float'),
    vHalf: attribute('aHalf', 'float'), vBank: attribute('aBank', 'float'), vRibW: positionWorld.xz.toVar(),
  });
  onColor(mat, () => {
    const c = attrs(), { vAcross, vAlong, vKind, vHalf, vBank, vRibW } = c;
    for (const f of R.head) f(c);
    const k = (c.k = floor(vKind.add(0.5)).toVar());
    const a = (c.a = abs(vAcross).toVar()), h = (c.h = max(vHalf, 0.1).toVar());
    const wob = (c.wob = grdNoise(vec2(vAlong.mul(0.35), sign(vAcross).mul(3.1))).sub(0.5).mul(0.55).add(grdNoise(vRibW.mul(1.7)).sub(0.5).mul(0.25)).toVar()); // uneven edges
    const fine = (c.fine = (R.fine ? R.fine(c) : grdNoise(vRibW.mul(3.7)).mul(0.6).add(grdNoise(vRibW.mul(9.1)).mul(0.4))).toVar());
    const cc = (c.col = vec3(U.uBank).toVar());
    const alpha = (c.alpha = float(1.0).toVar()); // edges fade into whatever the ground shows there (canopy shade, drifts): no outline
    ice.assign(0.0);
    If(k.lessThan(0.5), () => { // ploughed road
      const surf = (c.surf = (R.edge ? R.edge(c) : smoothstep(h.sub(0.35), h.add(0.15), a.add(wob)).oneMinus()).toVar());
      const road = (R.road ? R.road(c) : roadDefault(c)).toVar();
      const bank = mix(U.uBankIn, U.uBank, smoothstep(0.15, 0.85, a.sub(h).div(1.2))).mul(fine.mul(0.04).add(0.98));
      cc.assign(mix(bank, road, surf));
      const outer = vHalf.add(U.uBankW); // bank foot (geometry); fade the last metre
      alpha.assign(smoothstep(outer.sub(U.uBankFade).add(wob.mul(0.3)), outer, a).mul(surf.oneMinus()).oneMinus()); // VIS-228: the last roads.bank.fade m (0.45; was 1.1: the bank's crest and outer face were see-through, a pale band)
      alpha.assign(mix(alpha, surf, smoothstep(0.02, 0.15, vBank).oneMinus())); // no bank (junctions, ends): fade at the road edge (VIS-228: only where the bank is down to ~5 cm: from 0.33 a bank still 15 cm high vanished, a blunt block end)
    }).ElseIf(k.greaterThan(5.5), () => { // ice lane: one smooth sheet level with the snow, no tyre tracks or grooves
      const px = length(fwidth(vRibW)).toVar(); // m per pixel: details fade out before they could alias into speckle
      // clear dark ice and greyer patches (frozen in layers), whiter streaks of snow dust along the lane where the skaters
      // go, a frosted rim where it meets the snow
      const cloud = grdNoise(vRibW.mul(0.19).add(7.0)).mul(0.65).add(grdNoise(vRibW.mul(0.7).add(3.0)).mul(0.35));
      const dust = smoothstep(0.55, 0.85, grdNoise(vec2(vAlong.mul(0.05), vAcross.mul(0.9).add(4.0)))).mul(0.4).toVar();
      // skate scratches: thin pale curves, mostly along the lane (isolines of noise stretched along it), never thinner
      // than about a pixel (fainter instead: the same mean from afar), gone from 25 cm a pixel
      const s1 = abs(grdNoise(vec2(vAlong.mul(0.11), vAcross.mul(2.4).add(1.0))).sub(0.5)), s2 = abs(grdNoise(vec2(vAlong.mul(0.08).add(5.0), vAcross.mul(1.9).add(vAlong.mul(0.25)))).sub(0.5));
      const sw = max(float(0.012), px.mul(1.2)).toVar(); // the line's half width in noise units
      const scr = smoothstep(0.0, sw, min(s1, s2)).oneMinus().mul(float(0.012).div(sw)).mul(smoothstep(0.12, 0.25, px).oneMinus()).mul(0.55);
      const rim = smoothstep(h.mul(0.62), h.mul(0.95), a.add(wob.mul(0.3))).toVar();
      cc.assign(mix(U.uIce.mul(cloud.sub(0.5).mul(0.3).add(1.0)), U.uBankIn, max(max(dust, scr), rim.mul(0.85))));
      alpha.assign(smoothstep(h.mul(0.85), h.add(0.3), a.add(wob.mul(0.3))).oneMinus());
      ice.assign(rim.oneMinus().mul(dust.mul(-1.5).add(1.0)));
    }).ElseIf(k.lessThan(1.5).or(k.greaterThan(3.5)), () => { // footpath / track: trodden band, no edge line
      cc.assign(select(k.greaterThan(3.5), U.uRoad, U.uPath).mul(fine.mul(0.08).add(0.95)).mul(grdNoise(vRibW.mul(2.3)).mul(0.06).add(0.97)));
      alpha.assign(smoothstep(h.mul(0.35), h.add(0.45), a.add(wob.mul(0.6))).oneMinus());
    }).ElseIf(k.lessThan(2.5), () => { // groomed cross-country ski trail (ground.look.ski, skiLook): classic tracks + skate lane
      const S = U.uSki; // x grooves, y cord, z tone, w edge
      const sharp = smoothstep(0.03, 0.12, fwidth(vAcross)).oneMinus().toVar(); // 1 near; from afar (> 12 cm a pixel) grooves and ribs show as their average
      // the classic pairs as the groomer lays them (skiLook): one in the middle under 2.5 m, else along the right-hand
      // edge (across < 0), and from 4.5 m along both edges; each pair = two grooves 7 cm wide, 20 cm apart
      const c1 = select(h.lessThan(1.25), float(0.0), h.sub(0.55).negate());
      const dp = min(abs(vAcross.sub(c1)), select(h.lessThan(2.25), float(99.0), abs(vAcross.add(c1)))).toVar(); // m from the nearest pair's centre
      const groove = mix(smoothstep(0.14, 0.24, dp).oneMinus().mul(0.35), smoothstep(0.02, 0.045, abs(dp.sub(0.1))).oneMinus(), sharp).mul(S.x);
      const skate = select(h.lessThan(1.25), float(0.0), smoothstep(0.28, 0.4, dp)); // the corduroy lane beside the tracks
      const cord = sin(vAcross.mul(6.2831 / 0.09)).mul(sharp).mul(skate).mul(S.y);
      cc.assign(U.uTrail.mul(S.z).mul(cord.mul(0.05).add(fine.mul(0.03)).add(0.985)).mul(groove.mul(-0.22).add(1.0)));
      alpha.assign(smoothstep(h.sub(S.w), h.add(0.08), a.add(wob.mul(0.15))).oneMinus()); // a groomed, nearly straight edge
    }).Else(() => { // snowmobile: tread bands across the track
      const tread = sin(vAlong.mul(6.2831 / 0.075)).mul(0.5).add(0.5);
      cc.assign(U.uPath.mul(tread.mul(0.04).add(0.96).add(fine.mul(0.03))));
      alpha.assign(smoothstep(h.mul(0.6), h.add(0.35), a.add(wob.mul(0.5))).oneMinus());
    });
    for (const f of R.extra) f(c);
    const A = diffuseColor.a.mul(clamp(alpha, 0.0, 1.0)).mul(attribute('aEnd', 'float')).toVar(); // VIS-228: a line's ends fade out
    // alpha to coverage has 4 hard steps (4x MSAA): a soft edge drew as 4-5 banded contours along every road and snow
    // bank. +- half a step per pixel (a fixed screen pattern, so a still view redraws bit for bit) gives back the ramp,
    // as the trees' impostors do (WS-75); the opaque core and the clear outside stay exact.
    const dz = ign(screenCoordinate.xy.add(17.3)).sub(0.5).mul(0.25);
    diffuseColor.assign(vec4(cc, select(A.greaterThan(0.0).and(A.lessThan(1.0)), clamp(A.add(dz), 0.0, 1.0), A)));
  }, 10);
  onRough(mat, (r) => {
    const c = attrs();
    const o = R.rough ? R.rough(r, c) : select(c.vKind.lessThan(0.5), mix(r, 0.55, smoothstep(0.08, 0.2, abs(abs(c.vAcross).sub(U.uGauge))).oneMinus().mul(U.uRuts)), r);
    return select(c.vKind.greaterThan(5.5), mix(o, clamp(length(fwidth(c.vRibW)).mul(1.2), 0.07, 0.3), ice), o); // ice: a polished sheet, blurred by distance (no sparkle)
  }, 10);
  onVariants(mat, () => { for (const s of [specularColor, specularColorBlended]) s.assign(mix(s, vec3(ICE_F0), ice)); }); // the ice mirrors the sky by day and the lamps at night
  onLight(mat, (ctx) => { ctx.outgoingLight.addAssign(ctx.reflectedLight.indirectSpecular.mul(ice).mul(ICE_SKY)); }); // its sky sheen
  return R;
}
